/* eslint-disable arrow-body-style */

import React from 'react';
import moment from 'moment';
import { TableColumn } from 'tea-component';
import UpdatesInputs from '../components/UpdatesInputs';
import Updates from '../components/Updates';
import { generateMockId ,
  getFileAndSuffixByFileName,
} from '@gp/utils/common';
// @ts-ignore
import { storage } from '@gp/utils/utils';
import { toProjectDetails } from '@gp/utils/project';
import { getIsDisabled } from '../../../../utils';
import { buildProjectSelectColumn, buildProjectColumn } from '../../../../utils/columns';
import { PROJECT_DETAIL_STORAGE_KEY } from '@gp/constants/storageKeys';
import { SMALL_WIDTH_COLUMN_WIDTH, LARGE_WIDTH_COLUMN_WIDTH } from '../../../../constants';
import { DataType } from '@gp/enums/common';
import type { IDPSReportCalendarProjectSimpleVO } from '@gp/types/dpsReport/common';
import type {
  IDPSReportUpdatesOfTheWeekVO,
  IDPSReportUpdatesOfTheWeekChangeVO,
} from '@gp/types/dpsReport/v3';
import { getBasePath } from '@/utils/utils';
import {  COMMON_IMAGE_SUFFIXES } from '@gp/constants/const';
export const CARD_TITLE = 'Updates of the Week';

const getNavigateChange = (
  changes?: IDPSReportUpdatesOfTheWeekChangeVO[] | null,
): IDPSReportUpdatesOfTheWeekChangeVO | null => {
  if (!Array.isArray(changes) || !changes.length) return null;
  let result: IDPSReportUpdatesOfTheWeekChangeVO | null = null;

  for (const change of changes) {
    const { change_type: changeType, content, update_time: updateTime } = change || {};
    if (!changeType || !Array.isArray(content) || !content.length) continue;
    // milestone date的变动、milestone finance的变动
    if ([1, 2].includes(changeType)) {
      if (result === null) {
        result = change;
        continue;
      }

      const { update_time: resultUpdateTime } = result || {};
      const changeUpdateTimeM = !updateTime ? null : moment(updateTime);
      const resultUpdateTimeM = !resultUpdateTime ? null : moment(resultUpdateTime);
      if (
        changeUpdateTimeM?.isValid() &&
        resultUpdateTimeM?.isValid() &&
        changeUpdateTimeM.isAfter(resultUpdateTimeM)
      ) {
        result = change;
        continue;
      }
    }

    // update info的变动
    if (changeType === 3) {
      if (result === null) {
        result = change;
      }
    }
  }

  return result;
};

const toProjectDetailsInUpdatesOfTheWeek = (
  cvm: IDPSReportUpdatesOfTheWeekVO,
  e: React.MouseEvent<Element, MouseEvent> | null,
  isJump: boolean,
) => {
  const { project_id: projectId, project, changes } = cvm || {};
  const { change_type: changeType = Number.NaN, domain_id: domainId = '' } =
    getNavigateChange(changes) || {};
  let tab = 'gameDetail';
  // milestone date的变动、milestone finance的变动
  if ([1, 2].includes(changeType)) {
    tab = 'milestone';
    if (isJump && !!domainId) {
      storage.setSessionStorageItem(
        PROJECT_DETAIL_STORAGE_KEY.GPOP_CACHE_MILESTONE_ID_STORAGE_KEY,
        domainId,
      );
    }
  } else if (changeType === 3) {
    // update info的变动
    tab = 'projectUpdate';
    if (isJump && !!domainId) {
      storage.setSessionStorageItem(
        PROJECT_DETAIL_STORAGE_KEY.GPOP_CACHE_UPDATE_ID_STORAGE_KEY,
        domainId,
      );
    }
  }
  const params = {
    id: projectId as any,
    name: project,
    tab,
  };
  return toProjectDetails(params, e, isJump);
};

const ProjectColumn = buildProjectColumn<IDPSReportUpdatesOfTheWeekVO>({
  header: 'Project',
  width: SMALL_WIDTH_COLUMN_WIDTH,
  toDetails: toProjectDetailsInUpdatesOfTheWeek,
});

export const buildDefaultChanges = () => [
  {
    change_type: 1,
    domain_id: '',
    content: [],
    update_time: '',
  },
  {
    change_type: 2,
    domain_id: '',
    content: [],
    update_time: '',
  },
  {
    change_type: 3,
    domain_id: '',
    content: [],
    update_time: '',
  },
  {
    change_type: 4,
    domain_id: '',
    content: [],
    update_time: '',
  },
];

export const buildRecord = (): IDPSReportUpdatesOfTheWeekVO => ({
  mock_id: generateMockId(),
  data_type: DataType.ManualAdded,
  project_id: '',
  project: '',
  platform: [],
  image_name: '',
  cover: '',
  priority: '',
  changes: buildDefaultChanges(),
});

export const getColumnsEditable = (props: {
  projects?: IDPSReportCalendarProjectSimpleVO[] | null;
  onProjectChange?: (cvm: IDPSReportUpdatesOfTheWeekVO, payload: string) => void;
  onChangesChange?: (
    cvm: IDPSReportUpdatesOfTheWeekVO,
    payload: IDPSReportUpdatesOfTheWeekChangeVO[],
  ) => void;
}) => {
  const { projects, onProjectChange, onChangesChange } = props || {};
  const projectsOptions = projects?.map((project) => {
    const { project_id: projectId, project: projectName } = project || {};
    return { text: projectName || '', value: projectId || '' };
  });

  return [
    buildProjectSelectColumn({
      width: SMALL_WIDTH_COLUMN_WIDTH,
      showTextWhenSelected: false,
      options: projectsOptions || [],
      onChange: (payload, cvm) => {
        onProjectChange?.(cvm, payload);
      },
    }),
    {
      key: 'changes',
      align: 'left',
      header: 'Evident Major Changes',
      width: LARGE_WIDTH_COLUMN_WIDTH,
      render: (cvm) => {
        const { data_type: dataType, project_id: projectId, changes } = cvm || {};
        const disabled = getIsDisabled(cvm);
        return (
          <UpdatesInputs
            disabled={disabled}
            dataType={dataType}
            projectId={projectId}
            value={changes || []}
            onChange={(payload) => {
              onChangesChange?.(cvm, payload);
            }}
          />
        );
      },
    },
  ] as TableColumn<IDPSReportUpdatesOfTheWeekVO>[];
};

export const getColumnsReadonly = (): TableColumn<IDPSReportUpdatesOfTheWeekVO>[] => [
  {
    ...ProjectColumn,
    render: (cvm, ...restParams) => {
      // @ts-ignore
      return ProjectColumn?.render(cvm, ...restParams);
    },
  },
  {
    key: 'changes',
    header: 'Evident Major Changes',
    width: LARGE_WIDTH_COLUMN_WIDTH,
    render: (cvm) => {
      const { project_id: projectId, project: projectName, changes } = cvm;
      const hasContent = changes?.some((change) => change?.content?.some((item) => !!item));
      if (!hasContent) return <span>-</span>;
      return <Updates projectId={projectId} projectName={projectName} list={changes} />;
    },
  },
];

const basePath = getBasePath();

const getPreviewerUrl =  (
  path?: string,
  name?: string,
  format?: string,
) => {
  // if (COMMON_IMAGE_SUFFIXES.includes(format || '')) {
  //   return 'few';
  // }
  const { protocol, host } = window.location;
  const urlSearchParamsTmp = new URLSearchParams({
    // 预览类型，用于区分进入对应的预览页
    previewer_type: 'dps_report_memo_materials',
    // 预览文档的COS存储path
    doc_key: path || '',
    // 预览文档的名称
    doc_name: name || '',
    // 预览文档的文件后缀
    doc_suffix: format || '',
    // upload_folder_name: uploadFolderName || '',
  });
  return `${protocol}//${host}${basePath}/g-pop/DocPreviewer?${urlSearchParamsTmp.toString()}`;
};

// 设置附件地址
export const setNewFileUrlChanges = (changes: any) => {
  if(changes && Array.isArray(changes)) {
    return changes.map((change: any) => {
      // 处理每个change项
      if(change.changes && Array.isArray(change.changes)) {
        return {
          ...change,
          changes: setNewFileUrlChangesContent(change.changes),
        };
      }
      return change;
    });
  }
  return changes;
};

// 设置附件地址，编辑状态
export const setNewFileUrlChangesContent = (changeList: any) => {
  if(changeList && Array.isArray(changeList)) {
    return changeList.map((item: any) => {
      const { materials, change_type: changeType, content, key_update: keyUpdate } = item;
      // 处理每个变更项
      if(changeType === 3) {
        const contentValue = content || [];
        if(Array.isArray(item.materials) && item.materials?.length) {
          contentValue.push('<p>Materials:</p>');
          materials?.forEach((subMater: any) => {
            const { path, name: fileName } = subMater;
            const { name, format } = getFileAndSuffixByFileName(fileName);
            const url = getPreviewerUrl(path, name, format);
            const urlTemp = `<p>${fileName} (<a href="${url}" target="_blank">${url}</a>)</p>`;
            contentValue.push(urlTemp);
          });
        }
        if(keyUpdate) {
          contentValue.push(keyUpdate);
        }
        return {
          ...item,
          content: contentValue
        };
      }
      return item;
    });
  }
  return changeList;
};

